/**
 * useBarcaChat - Public API hook for controlling the Barca Chat Widget
 * Provides methods to programmatically control the widget
 */

import { useState, useCallback } from 'react';
import { useConversation } from '@elevenlabs/react';
import { BarcaChatAPI, ConversationStatus } from '../types';
import { useBarcaChatConfig } from '../providers/BarcaChatProvider';

export const useBarcaChat = (): BarcaChatAPI => {
    const { config } = useBarcaChatConfig();
    const [isOpen, setIsOpen] = useState(false);
    const conversation = useConversation();

    const openWidget = useCallback(() => {
        setIsOpen(true);
        config.onWidgetOpen?.();
    }, [config]);

    const closeWidget = useCallback(() => {
        setIsOpen(false);
        config.onWidgetClose?.();
    }, [config]);

    const startVoiceChat = useCallback(async () => {
        try {
            await conversation.startSession({
                agentId: config.agentId,
            } as any);
            config.onConversationStart?.();
        } catch (error) {
            const err = error instanceof Error ? error : new Error('Failed to start conversation');
            config.onError?.(err);
            throw err;
        }
    }, [conversation, config]);

    const endVoiceChat = useCallback(async () => {
        try {
            await conversation.endSession();
            config.onConversationEnd?.();
        } catch (error) {
            const err = error instanceof Error ? error : new Error('Failed to end conversation');
            config.onError?.(err);
            throw err;
        }
    }, [conversation, config]);

    // Map ElevenLabs status to our status
    const getConversationStatus = (): ConversationStatus => {
        switch (conversation.status) {
            case 'connected':
                return 'connected';
            case 'connecting':
                return 'connecting';
            case 'disconnected':
                return 'disconnected';
            default:
                return 'idle';
        }
    };

    return {
        openWidget,
        closeWidget,
        isOpen,
        startVoiceChat,
        endVoiceChat,
        conversationStatus: getConversationStatus(),
    };
};
